import {hasServerFlag, SERVER_FLAGS} from '../../../../../config';
import {type RunsLinePlotConfig} from '../../../../PanelRunsLinePlot/types';
import {
  type DerivedPointVisualizationOption,
  isPointVizFullFidelity,
  type LinePlotSettings,
} from '../../types';
import {
  DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS,
  DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING,
  DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS,
  DEFAULT_PANEL_MAX_NUM_RUNS,
} from '../maxRunDefaults';

export const getCascadingMaxRuns = ({
  canOverrideMaxRuns,
  pointVisualizationMethod,
  maxRuns,
  limit,
}: {
  canOverrideMaxRuns?: boolean;
  pointVisualizationMethod: DerivedPointVisualizationOption;
  maxRuns: LinePlotSettings['maxRuns'];
  limit?: RunsLinePlotConfig['limit'];
}) => {
  const isBucketing = isPointVizFullFidelity(pointVisualizationMethod);

  const maxValue = getMaxRunsCeiling({
    canOverrideMaxRuns: canOverrideMaxRuns ?? false,
    isBucketing,
  });

  const passedLimit = limit ?? maxRuns;
  if (passedLimit == null) {
    return undefined;
  }

  // if both are undefined above than we'll have a value here even if TS can't pick it up
  return Math.min(maxValue, (limit ?? maxRuns) as number);
};

/**
 * Return the hard upper bound on the number of runs a user can configure
 * for a chart. This is a *policy* function — it doesn't read or modify the
 * user's saved setting; it only determines the ceiling that other functions
 * (getCascadingMaxRuns, clampMaxRunsForLinePlot) clamp against.
 *
 * Priority order for bucketing mode:
 * 1. Server flag limit — if set, it always wins (server environments may be less performant).
 * 2. Experimental ramp flag — returns DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS
 *    when `canOverrideMaxRuns` is true.
 * 3. Default — DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS (50).
 *
 * Non-bucketing (sampling) mode bypasses all of the above and returns either
 * DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING (line plots) or DEFAULT_PANEL_MAX_NUM_RUNS (other panels).
 *
 */
export const getMaxRunsCeiling = ({
  canOverrideMaxRuns,
  isBucketing,
  isLinePlot = false,
}: {
  canOverrideMaxRuns: boolean;
  isBucketing: boolean;
  isLinePlot?: boolean;
}) => {
  // these limits only apply to bucketing
  // sampling limits are exempt
  if (!isBucketing) {
    return isLinePlot
      ? DEFAULT_LINE_PLOT_NUM_RUNS_SAMPLING
      : DEFAULT_PANEL_MAX_NUM_RUNS;
  }
  // if it's FF fidelity it's a line plot AND the server limit applies
  const hasServerLimit = hasServerFlag(SERVER_FLAGS.maxRunsLimit);

  // this gets kind of weird - the default hardcoded value is a check against a config flag
  // ```
  // DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS = serverFlagMaxRunsLimit() // has a default if undefined
  // ```
  // this means that IF a server limit is configured for an environment, it will show up as this value
  // however since it's defaulted we have to check if the underlying config value exists before we use it
  // This way we can fall through when there's not an active server flag limit and check for the ramp flag
  // but then ultimately we'll end up returning the defaulted value
  const serverLimitOverride = hasServerLimit
    ? DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS
    : undefined;

  // if this value isn't undefined it's because there is a set server limit, and we'll use that
  if (serverLimitOverride) {
    return serverLimitOverride;
  }

  // otherwise allow the experimental ramp flag to override the limit
  if (canOverrideMaxRuns) {
    return DEFAULT_OVERRIDE_MAX_BUCKETED_RUNS;
  }

  // no overrides active — use the base default (50)
  return DEFAULT_LINE_PLOT_MAX_NUM_BUCKETED_RUNS;
};

/**
 * Clamp a panel-level max-runs value to the line-plot-specific ceiling.
 * Used during settings inheritance (usePanelInheritedSettings) to ensure
 * that a panel's resolved `limit` never exceeds what the current
 * environment and feature flags allow for line plots.
 */
export const clampMaxRunsForLinePlot = (
  {
    isBucketing,
    canOverrideMaxRuns,
  }: {
    isBucketing: boolean;
    canOverrideMaxRuns: boolean;
  },
  limitFromPanelConfig?: number
) => {
  const maxRunLimit = getMaxRunsCeiling({
    canOverrideMaxRuns,
    isBucketing,
    isLinePlot: true,
  });
  return clampToLimit(limitFromPanelConfig, maxRunLimit);
};

/** Clamp a value to an upper bound, passing through `undefined` unchanged. */
export const clampToLimit = (value: number | undefined, limit: number) => {
  if (value) {
    return Math.min(value, limit);
  }
  return value;
};
